import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Select,
  Button,
  Card,
  CardContent,
  InputLabel,
  FormControl,
  Divider,
  Grid2,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import alertConfigStore from "../../stores/AlertConfigStore";
import defaultConfigStore from "../../stores/DefaultConfigStore";
import { ALERT_FIELD_RULES } from "./constants/ALERT_FIELD_RULES";
import { DEVICE_ALERT_TEMPLATE } from "./constants/DEVICE_ALERT_TEMPLATES";
import AlertFieldEditor from "./config-editor-components/AlertFieldEditor";
import ValveGroupEditor from "./config-editor-components/ValveGroupEditor";
import TransducerFaultEditor from "./config-editor-components/TransducerFaultEditor";
import { toast } from "react-toastify";

const ConfigEditor = observer(({ mode = "device" }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isDefault = mode === "default";
  const deviceRecord = isDefault
    ? location.state?.deviceTypeRecord
    : location.state.deviceRecord;

  const alertConfigId = deviceRecord?.alert_config_id;
  console.log("Here is alertConfigId: ", alertConfigId);

  const deviceName =
    deviceRecord?.name || deviceRecord?.type_name || "Unknown Device";

  const { id: paramId } = useParams();
  const deviceId = deviceRecord?.pimios_id || deviceRecord?.id || paramId;

  console.log("Editing config for deviceId:", deviceId);

  const normalizedId = String(deviceId).trim();

  const store = isDefault ? defaultConfigStore : alertConfigStore;
  console.log(
    "Using store:",
    isDefault ? "defaultConfigStore" : "alertConfigStore"
  );

  const [form, setForm] = useState({
    recipients: { email: [], sms: [] },
    alertsConfig: {},
  });
  const [availableFields, setAvailableFields] = useState([]);

  useEffect(() => {
    const config = isDefault
      ? store.getDefaultConfig(normalizedId)
      : store.getAlertConfigByObjectId(alertConfigId);

    if (config) {
      setForm({
        recipients: {
          email: config.recipients?.email || [],
          sms: config.recipients?.sms || [],
        },
        alertsConfig: config.alertsConfig,
      });
    } else {
      alertConfigStore.fetchAlertConfigById(alertConfigId).then(() => {
        const fetched = alertConfigStore.getAlertConfig(alertConfigId);
        if (fetched) {
          setForm({
            recipients: {
              email: fetched.recipients?.email || [],
              sms: fetched.recipients?.sms || [],
            },
            alertsConfig: fetched.alertsConfig,
          });
        }
      });
    }
  }, [alertConfigId]);

  useEffect(() => {
    const currentFields = Object.keys(form.alertsConfig);
    const remaining = Object.entries(ALERT_FIELD_RULES)
      .filter(([field]) => !currentFields.includes(field))
      .map(([field, rule]) => ({ field, label: rule.label }));
    setAvailableFields(remaining);
  }, [form.alertsConfig]);

  const handleAddField = (field) => {
    const rule = ALERT_FIELD_RULES[field];
    const defaultValue = rule.valveGroup
      ? { v1: getDefaultRule(rule), v2: getDefaultRule(rule) }
      : rule.transducerGroup
        ? DEVICE_ALERT_TEMPLATE.transducerSides.reduce((acc, side) => {
            acc[side] = {};
            for (let i = 1; i <= DEVICE_ALERT_TEMPLATE.transducerCount; i++) {
              acc[side][`transducer${i}`] = getDefaultRule(rule);
            }
            return acc;
          }, {})
        : getDefaultRule(rule);

    setForm((prev) => ({
      ...prev,
      alertsConfig: { ...prev.alertsConfig, [field]: defaultValue },
    }));
  };

  const handleRemoveField = (field) => {
    const updated = { ...form.alertsConfig };
    delete updated[field];
    setForm((prev) => ({ ...prev, alertsConfig: updated }));
  };

  const handleFieldUpdate = (field, value) => {
    setForm((prev) => ({
      ...prev,
      alertsConfig: { ...prev.alertsConfig, [field]: value },
    }));
  };

  const handleAddEmail = (value) => {
    if (!value.trim()) return;
    setForm((prev) => ({
      ...prev,
      recipients: {
        ...prev.recipients,
        email: [...prev.recipients.email, value.trim()],
      },
    }));
  };

  const handleAddSMS = (value) => {
    if (!value.trim()) return;
    setForm((prev) => ({
      ...prev,
      recipients: {
        ...prev.recipients,
        sms: [...prev.recipients.sms, value.trim()],
      },
    }));
  };

  const handleSave = async () => {
    try {
      if (isDefault) {
        await store.updateDefaultConfig(deviceId, form);

        toast.success("Default config saved!");
        navigate(`/alerts/default-config/${deviceId}`, {
          state: { deviceTypeRecord: deviceRecord, mode: "default" },
        });
      } else {
        let updatedId = alertConfigId;

        const existingConfig = store.getAlertConfig(deviceId);

        if (existingConfig) {
          await store.updateAlertConfig(deviceId, form);
          updatedId = existingConfig.alert_config_id;
        } else {
          const result = await store.createAlertConfig({
            device_id: deviceId,
            ...form,
          });

          updatedId = result?.alert_config_id || result?._id;
        }

        if (!updatedId) throw new Error("Missing alert config ID");

        toast.success("Configuration saved!");
        navigate(`/alerts/config/${deviceId}`, {
          state: {
            deviceRecord: {
              ...deviceRecord,
              alert_config_id: updatedId,
            },
          },
        });
      }
    } catch (err) {
      console.error("Save failed", err);
      toast.error("Failed to save configuration.");
    }
  };

  const getDefaultRule = (rule) => ({
    type: rule.defaultType,
    triggerMessage: "",
    ...(rule.defaultType === "one-bound" && { threshold: 0, boundary: "min" }),
    ...(rule.defaultType === "two-bound" && { low: 0, high: 0 }),
    ...(rule.defaultType === "fault-code" && { faultCode: "" }),
  });

  return (
    <Box sx={{ width: "100%" }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          {alertConfigId
            ? `Edit Alert Configuration - ${deviceName}`
            : `Create Alert Configuration - ${deviceName}`}
        </Typography>
      </Box>

      {!isDefault && (
        <Card
          elevation={4}
          sx={{
            mb: 4,
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid #E0E6ED",
            boxShadow: "0px 3px 10px rgba(0, 0, 0, 0.06)",
          }}
        >
          <Box
            sx={{
              background: "#1E40AF",
              color: "#fff",
              px: 3,
              py: 2,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h7"
              fontWeight={600}
              sx={{ letterSpacing: 0.5 }}
            >
              Alert Recipients
            </Typography>
          </Box>

          <CardContent sx={{ backgroundColor: "#F9FAFB", pt: 4, pb: 3 }}>
            <Grid2 container spacing={4} direction="column">
              {/* Email Recipients */}
              <Grid2 item xs={12} md={6}>
                <Typography fontWeight={600} gutterBottom>
                  Email Recipients
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid #cfd8dc",
                    borderRadius: 1,
                    p: 1,
                    minHeight: "56px",
                    maxHeight: 150,
                    overflowY: "auto",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {form.recipients.email.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      onDelete={() => {
                        setForm((prev) => ({
                          ...prev,
                          recipients: {
                            ...prev.recipients,
                            email: prev.recipients.email.filter(
                              (_, i) => i !== index
                            ),
                          },
                        }));
                      }}
                      sx={{ bgcolor: "#e3f2fd" }}
                    />
                  ))}
                </Box>
                <TextField
                  label="Add Email"
                  placeholder="e.g. user@example.com"
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddEmail(e.target.value);
                      e.target.value = "";
                    }
                  }}
                  helperText="Press Enter to add"
                  sx={{ mt: 2 }}
                />
              </Grid2>

              {/* SMS Recipients */}
              <Grid2 item xs={12} md={6}>
                <Typography fontWeight={600} gutterBottom>
                  SMS Recipients
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid #cfd8dc",
                    borderRadius: 1,
                    p: 1,
                    minHeight: "56px",
                    maxHeight: 150,
                    overflowY: "auto",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {form.recipients.sms.map((sms, index) => (
                    <Chip
                      key={index}
                      label={sms}
                      onDelete={() => {
                        setForm((prev) => ({
                          ...prev,
                          recipients: {
                            ...prev.recipients,
                            sms: prev.recipients.sms.filter(
                              (_, i) => i !== index
                            ),
                          },
                        }));
                      }}
                      sx={{ bgcolor: "#fce4ec" }}
                    />
                  ))}
                </Box>
                <TextField
                  label="Add SMS"
                  placeholder="e.g. 1234567890"
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSMS(e.target.value);
                      e.target.value = "";
                    }
                  }}
                  helperText="Press Enter to add"
                  sx={{ mt: 2 }}
                />
              </Grid2>
            </Grid2>
          </CardContent>
        </Card>
      )}

      <Divider sx={{ mb: 3 }} />

      {Object.entries(form.alertsConfig).map(([field, config]) => {
        const rule = ALERT_FIELD_RULES[field];
        if (!rule) return null;
        const Component = rule.valveGroup
          ? ValveGroupEditor
          : rule.transducerGroup
            ? TransducerFaultEditor
            : AlertFieldEditor;

        return (
          <Card
            key={field}
            elevation={3}
            sx={{
              mb: 3,
              borderRadius: 2,
              backgroundColor: "#fafbfd",
            }}
          >
            <Box
              sx={{
                borderBottom: "1px solid #e0e0e0",
                backgroundColor: "#e8f0fe",
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                px: 2,
                py: 1,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" color="primary">
                {rule.label} ({field})
              </Typography>
            </Box>
            <CardContent>
              <Component
                field={field}
                config={config}
                rule={rule}
                onChange={(val) => handleFieldUpdate(field, val)}
                onDelete={
                  rule.allowDelete ? () => handleRemoveField(field) : null
                }
              />
            </CardContent>
          </Card>
        );
      })}

      {availableFields.length > 0 && (
        <FormControl fullWidth sx={{ mt: 2, mb: 4 }}>
          <InputLabel>Add Alert Field</InputLabel>
          <Select
            label="Add Alert Field"
            onChange={(e) => e.target.value && handleAddField(e.target.value)}
          >
            <MenuItem value="">Select field</MenuItem>
            {availableFields.map(({ field, label }) => (
              <MenuItem key={field} value={field}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Box display="flex" justifyContent="flex-end">
        <Button
          onClick={handleSave}
          variant="contained"
          size="large"
          sx={{
            backgroundColor: "#1976d2",
            ":hover": { backgroundColor: "#125ea5" },
            px: 4,
            py: 1.5,
            borderRadius: 2,
          }}
        >
          Save Configuration
        </Button>
      </Box>
    </Box>
  );
});

export default ConfigEditor;
